import React, { createContext, useContext, useState } from 'react';

const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

const defaultUser = {
  name: 'Jane & John',
  email: 'couple@email.com',
  avatar: '',
  isAuthenticated: false,
};

const defaultWedding = {
  date: '',
  venue: '',
  tasks: [],
  vendors: [],
  budget: [],
  guests: [],
  eventSchedule: [], // { time, title, description, location }
  collaborators: [], // { name, email }
  collabNotes: [], // { sender, message, timestamp }
  inspirationBoard: [], // { image, note }
  seatingChart: [], // { name, guests: [guestName1, ...] }
  travelAccommodation: [], // { type: 'hotel'|'transport'|'tip', name, info, link }
};

export const AppProvider = ({ children }) => {
  // Load user from localStorage if present
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : defaultUser;
  });
  const [wedding, setWedding] = useState(defaultWedding);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [currency, setCurrency] = useState('USD'); // New currency state, default to USD

  // Auth
  const login = (email, password) => {
    setLoading(true);
    setTimeout(() => {
      const loggedInUser = { ...defaultUser, email, isAuthenticated: true };
      setUser(loggedInUser);
      localStorage.setItem('user', JSON.stringify(loggedInUser));
      setLoading(false);
    }, 1000);
  };
  const logout = () => {
    setUser({ ...defaultUser, isAuthenticated: false });
    localStorage.removeItem('user');
    setNotification({ type: 'info', message: 'Logged out.' });
  };

  // Wedding Data CRUD
  const addTask = (task) => setWedding(w => ({ ...w, tasks: [...w.tasks, task] }));
  const updateTask = (idx, updated) => setWedding(w => ({ ...w, tasks: w.tasks.map((t, i) => i === idx ? updated : t) }));
  const removeTask = (idx) => setWedding(w => ({ ...w, tasks: w.tasks.filter((_, i) => i !== idx) }));

  // Event Schedule CRUD
  const addScheduleItem = (item) => setWedding(w => ({ ...w, eventSchedule: [...w.eventSchedule, item] }));
  const updateScheduleItem = (idx, updated) => setWedding(w => ({ ...w, eventSchedule: w.eventSchedule.map((e, i) => i === idx ? updated : e) }));
  const removeScheduleItem = (idx) => setWedding(w => ({ ...w, eventSchedule: w.eventSchedule.filter((_, i) => i !== idx) }));

  // Collaboration CRUD
  const addCollaborator = (collab) => setWedding(w => ({ ...w, collaborators: [...w.collaborators, collab] }));
  const removeCollaborator = (idx) => setWedding(w => ({ ...w, collaborators: w.collaborators.filter((_, i) => i !== idx) }));
  const addCollabNote = (note) => setWedding(w => ({ ...w, collabNotes: [...w.collabNotes, note] }));

  // Inspiration Board CRUD
  const addInspiration = (item) => setWedding(w => ({ ...w, inspirationBoard: [...w.inspirationBoard, item] }));
  const removeInspiration = (idx) => setWedding(w => ({ ...w, inspirationBoard: w.inspirationBoard.filter((_, i) => i !== idx) }));

  // Seating Chart CRUD
  const addTable = (table) => setWedding(w => ({ ...w, seatingChart: [...w.seatingChart, table] }));
  const updateTable = (idx, updated) => setWedding(w => ({ ...w, seatingChart: w.seatingChart.map((t, i) => i === idx ? updated : t) }));
  const removeTable = (idx) => setWedding(w => ({ ...w, seatingChart: w.seatingChart.filter((_, i) => i !== idx) }));

  // Travel & Accommodation CRUD
  const addTravelItem = (item) => setWedding(w => ({ ...w, travelAccommodation: [...w.travelAccommodation, item] }));
  const removeTravelItem = (idx) => setWedding(w => ({ ...w, travelAccommodation: w.travelAccommodation.filter((_, i) => i !== idx) }));

  const addVendor = (vendor) => setWedding(w => ({ ...w, vendors: [...w.vendors, vendor] }));
  const updateVendor = (idx, updated) => setWedding(w => ({ ...w, vendors: w.vendors.map((v, i) => i === idx ? updated : v) }));
  const removeVendor = (idx) => setWedding(w => ({ ...w, vendors: w.vendors.filter((_, i) => i !== idx) }));

  const addBudget = (item) => setWedding(w => ({ ...w, budget: [...w.budget, item] }));
  const updateBudget = (idx, updated) => setWedding(w => ({ ...w, budget: w.budget.map((b, i) => i === idx ? updated : b) }));
  const removeBudget = (idx) => setWedding(w => ({ ...w, budget: w.budget.filter((_, i) => i !== idx) }));

  const addGuest = (guest) => setWedding(w => ({ ...w, guests: [...w.guests, guest] }));
  const updateGuest = (idx, updated) => setWedding(w => ({ ...w, guests: w.guests.map((g, i) => i === idx ? updated : g) }));
  const removeGuest = (idx) => setWedding(w => ({ ...w, guests: w.guests.filter((_, i) => i !== idx) }));

  // Notification
  const clearNotification = () => setNotification(null);

  return (
    <AppContext.Provider value={{
      user, setUser, login, logout,
      wedding, setWedding,
      addTask, updateTask, removeTask,
      addVendor, updateVendor, removeVendor,
      addBudget, updateBudget, removeBudget,
      addGuest, updateGuest, removeGuest,
      addScheduleItem, updateScheduleItem, removeScheduleItem,
      addCollaborator, removeCollaborator, addCollabNote,
      addInspiration, removeInspiration,
      addTable, updateTable, removeTable,
      addTravelItem, removeTravelItem,
      loading, notification, clearNotification,
      currency, setCurrency // Expose currency state
    }}>
      {children}
    </AppContext.Provider>
  );
};
